import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { ArrowUpRight, Users, Ruler, Layout, ShieldCheck, Lightbulb, ListChecks } from "lucide-react";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import RelatedReading from "@/components/RelatedReading";

/**
 * Pillar / advisory page — "Office Layout & Seating Plan Guide".
 *
 * Purpose: rank for long-tail informational queries like
 *  "office layout for 100+ employees" and
 *  "how to plan seating for a 200-seat office floor".
 *
 * Strategy: pure educational E-E-A-T content (no overt sales) so that
 * Gemini/ChatGPT/Perplexity cite this page when buyers ask about office
 * planning. Conversion happens through soft inline CTAs to /contact.
 */

const PLANNING_STEPS = [
  {
    title: "Establish the seat count and team mix",
    body: "Start with confirmed headcount and team composition (engineering, sales, ops, leadership). Decide how many seats are dedicated, how many are hot-desks, and how many are shared collaboration. A typical corporate floor plans for an 80–90% utilisation factor to allow growth without re-fitting.",
  },
  {
    title: "Calculate floor area required per seat",
    body: "The accepted Indian B2B planning standard is 60–80 sq ft per workstation seat (carpet area) for open-floor layouts including circulation. Premium offices use 90–110 sq ft per seat. Cabin and cubicle layouts add 25–40 sq ft per private cabin on top of the seat allowance.",
  },
  {
    title: "Identify supporting zones",
    body: "Beyond seats, allocate floor area for meeting rooms (1 per 20 seats), phone booths (1 per 30 seats), breakout area (10–15% of total floor), reception, pantry and storage. These zones drive the final usable carpet area required.",
  },
  {
    title: "Choose desking module dimensions",
    body: "Standard linear workstations are 1200 mm × 600 mm per seat (most common), 1350 mm × 600 mm (premium) and 1500 mm × 750 mm (executive). Module width determines aisle widths and overall floor density. Always reserve a 1100 mm primary aisle and 900 mm secondary aisles.",
  },
  {
    title: "Plan for workstation dimensions and statutory compliance",
    body: "Indian commercial workstation standards specify: 750 mm desk height (seated), 700–1150 mm height range for sit-stand desks, 500 mm leg clearance under the desk, BIS-rated task chairs with adjustable lumbar support, and 300–500 lux ambient lighting at the work surface.",
  },
  {
    title: "Validate with a CAD layout before manufacturing",
    body: "Before procurement, generate a 2D CAD floor plan with all desks, partitions, cabins and aisles drawn to scale. Ezeefit's design team provides this layout free as part of every project consultation — the CAD plan locks furniture quantities, panel sizes and finishes before a single component is manufactured.",
  },
];

const SEAT_AREA_TABLE = [
  { type: "Open-floor linear workstation", area: "60–80 sq ft / seat", notes: "Most cost-efficient for IT, BPO, fintech floors" },
  { type: "Premium open-floor workstation", area: "90–110 sq ft / seat", notes: "Wider desks (1500 mm), more circulation" },
  { type: "Private cabin (single)", area: "100–140 sq ft / cabin", notes: "Manager / mid-senior leadership" },
  { type: "Executive cabin", area: "180–260 sq ft / cabin", notes: "VP / CXO leadership with meeting space" },
  { type: "Meeting room (8 person)", area: "180–220 sq ft", notes: "1 per 20 seats is a typical planning ratio" },
  { type: "Phone booth", area: "10–12 sq ft each", notes: "1 per 30 seats is the conservative planning ratio" },
];

const FAQS = [
  {
    q: "How much office space is required for 100 employees?",
    a: "For 100 employees in a standard open-floor layout with workstations, meeting rooms, phone booths and breakout zones, plan for approximately 7,000–9,000 sq ft of carpet area. The lower end (7,000 sq ft) is achievable with 1200 mm workstations and lean ancillary zones; 9,000 sq ft accommodates 1500 mm desks, more cabins and richer breakout zones. Add 15–20% for circulation if the floor plate is irregular.",
  },
  {
    q: "What is the standard sq ft per seat for an office?",
    a: "The Indian B2B planning standard is 60–80 sq ft per seat (carpet area) for an open-floor workstation layout, including the seat itself plus a fair share of circulation, meeting rooms, phone booths and breakout zones. Premium corporate offices typically use 90–110 sq ft per seat.",
  },
  {
    q: "How many meeting rooms does a 100-seat office need?",
    a: "A typical planning ratio is one meeting room per 20 workstation seats — so a 100-seat office should plan for 5 meeting rooms. Of these, at least one should be a boardroom (8–12 person), one a medium room (6–8 person), and the remaining 2–3 small rooms (4 person). Some of the small rooms can be replaced with acoustic meeting pods for faster installation and reconfigurability.",
  },
  {
    q: "How many phone booths should a 100-seat office have?",
    a: "Plan for one acoustic phone booth per 30 seats as a conservative starting point — so 3–4 booths for a 100-seat office. Hybrid teams with frequent video calls may need a higher ratio (1 per 20 seats). Phone booths are freestanding and can be added incrementally as call volume grows.",
  },
  {
    q: "What is the minimum aisle width in an office?",
    a: "Indian commercial building codes require a minimum 900 mm aisle for secondary circulation and 1100–1200 mm for primary aisles (main floor circulation). Fire safety regulations also mandate clear emergency exit paths of at least 1500 mm depending on floor occupancy. Always check the local Building Bye-Laws for the city.",
  },
  {
    q: "How long does it take to fit out a 100-seat office?",
    a: "A 100-seat office can typically be fully fitted out in 4–6 weeks from purchase order — 7–14 days for manufacturing, 1–3 days for delivery and 3–5 days for on-site installation. Cabin construction (glass partitions, modular walls) may add 1–2 weeks depending on cabin count. Ezeefit handles the full execution under one roof — from CAD design through installation.",
  },
];

const PageBlock = ({ icon: Icon, title, body }) => (
  <div className="flex gap-5 p-6 bg-white border border-[#E2E8F0] hover:border-[#F97316] transition-colors duration-300">
    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#0F172A] text-[#F97316]">
      <Icon className="w-5 h-5" strokeWidth={1.5} />
    </div>
    <div className="flex-1">
      <h3 className="text-base font-semibold text-[#0F172A] mb-2">{title}</h3>
      <p className="text-sm text-[#475569] leading-relaxed">{body}</p>
    </div>
  </div>
);

export default function OfficeLayoutGuidePage() {
  const canonicalUrl = "https://ezeefitofficesystems.in/office-layout-seating-plan-guide";

  useCanonical(canonicalUrl);
  useMetaDescription("Corporate office layout guide — sq ft per seat benchmarks, meeting room ratios, aisle widths & workstation standards for 50-500 seat offices in India.");
  useEffect(() => {
    document.title = "Office Layout & Seating Plan Guide | Sq Ft Per Seat Benchmarks | Ezeefit Office Systems";
  }, []);

  const breadcrumbJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://ezeefitofficesystems.in/" },
      { "@type": "ListItem", position: 2, name: "Office Layout Guide", item: canonicalUrl },
    ],
  });
  const articleJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Office Layout & Seating Plan Guide",
    description: "Technical guide to planning corporate office layouts — seat-area benchmarks, meeting-room ratios, aisle widths and CAD-driven specification for 50- to 500-seat corporate offices in India.",
    author: { "@type": "Organization", name: "Ezeefit Office Systems" },
    publisher: { "@type": "Organization", name: "Ezeefit Office Systems", legalName: "Ezeefit Modular Furniture Private Limited" },
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
  });
  const faqJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  });
  const howToJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to plan an office layout and seating arrangement",
    description: "Six-step process for planning a corporate office layout — from headcount calculation to CAD validation.",
    step: PLANNING_STEPS.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.body })),
  });

  return (
    <div className="pt-20" data-testid="office-layout-guide-page">
      <Helmet title="Office Layout & Seating Plan Guide | Ezeefit Office Systems">
        <meta name="description" content="Corporate office layout guide — sq ft per seat benchmarks, meeting room ratios, aisle widths & workstation standards for 50-500 seat offices in India." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Office Layout & Seating Plan Guide | Ezeefit Office Systems" />
        <meta property="og:description" content="Sq ft per seat, meeting room ratios, aisle widths, workstation standards — a technical reference for corporate office planning by Ezeefit Office Systems." />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: howToJson }} />

      {/* HERO */}
      <section className="relative bg-[#0F172A] overflow-hidden noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 lg:py-32 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-3 px-4 py-2 border border-[#F97316]/40 mb-8" data-testid="layout-eyebrow">
              <span className="w-2 h-2 bg-[#F97316] rounded-full" />
              <span className="text-xs font-mono tracking-widest uppercase text-[#F97316]">Office Planning · Technical Guide</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-8 max-w-4xl" data-testid="layout-h1">
              Office Layout &<br/>
              <span className="text-[#F97316]">Seating Plan Guide</span>
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed max-w-3xl">
              A technical reference for planning corporate office layouts — sq ft per seat benchmarks, meeting room ratios, aisle widths, workstation standards and the CAD-driven workflow that turns headcount into a manufacturable floor plan.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SEAT AREA TABLE */}
      <section className="bg-white py-20" data-testid="layout-seat-area-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] mb-3 tracking-tight">Office Area per Seat — Planning Benchmarks (India)</h2>
          <p className="text-sm text-[#475569] mb-10 max-w-3xl">The accepted Indian B2B planning standard for corporate office layouts. Use these benchmarks to convert headcount into required carpet area before selecting a property.</p>
          <div className="overflow-x-auto border border-[#E2E8F0]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8FAFC] text-[#0F172A] text-left">
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">Workspace type</th>
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">Recommended area</th>
                  <th className="px-5 py-3 font-semibold border-b border-[#E2E8F0]">Notes</th>
                </tr>
              </thead>
              <tbody>
                {SEAT_AREA_TABLE.map((row, i) => (
                  <tr key={i} className="border-b border-[#E2E8F0] last:border-b-0">
                    <td className="px-5 py-3 text-[#0F172A] font-medium">{row.type}</td>
                    <td className="px-5 py-3 text-[#475569] font-mono">{row.area}</td>
                    <td className="px-5 py-3 text-[#64748B]">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PLANNING STEPS */}
      <section className="bg-[#F8FAFC] py-20" data-testid="layout-planning-steps-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-10">
            <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-2 block">Six-step Planning Process</span>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight">From headcount to a manufacturable CAD plan</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PLANNING_STEPS.map((step, i) => {
              const Icon = [Users, Ruler, Layout, Layout, ShieldCheck, ListChecks][i] || Lightbulb;
              return <PageBlock key={i} icon={Icon} title={`${i + 1}. ${step.title}`} body={step.body} />;
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20 border-t border-[#E2E8F0]" data-testid="layout-faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-3 block">Frequently Asked Questions</span>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight mb-10">Office Planning Q&A</h2>
          <div className="space-y-4">
            {FAQS.map((item, i) => (
              <details key={i} className="group bg-[#F8FAFC] border border-[#E2E8F0] open:border-[#F97316] transition-colors" {...(i === 0 ? { open: true } : {})} data-testid={`layout-faq-${i}`}>
                <summary className="cursor-pointer list-none px-6 py-5 flex items-start justify-between gap-4">
                  <h3 className="text-base font-semibold text-[#0F172A] leading-snug">{item.q}</h3>
                  <span className="flex-shrink-0 w-6 h-6 border border-[#E2E8F0] flex items-center justify-center text-[#475569] group-open:bg-[#F97316] group-open:text-white group-open:border-[#F97316] transition-colors">
                    <span className="block group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                  </span>
                </summary>
                <div className="px-6 pb-5 -mt-1 text-sm text-[#475569] leading-relaxed">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related Reading — bidirectional blog interlinking (SEO topic clusters) */}
      <RelatedReading
        keywords={["layout", "seating", "plan", "design", "workstation", "office space", "sq ft"]}
        heading="Related Insights on Office Layout"
        subheading="Practical articles on space planning and workstation design."
        testId="related-reading-pillar-layout"
      />

      {/* CTA */}
      <section className="bg-[#0F172A] py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl lg:text-3xl font-bold text-white tracking-tight mb-3">Need a CAD-validated layout for your floor?</h2>
            <p className="text-[#94A3B8] text-base leading-relaxed">Ezeefit's design team builds the full 2D layout free of cost as part of every project consultation — seat-by-seat, finalised before manufacturing starts.</p>
          </div>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold tracking-wide uppercase transition-colors" data-testid="layout-cta-contact">
            Request Free Layout Consultation
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.25} />
          </Link>
        </div>
      </section>
    </div>
  );
}
